/**
 * Competitor sellers on a listing.
 *
 * The Product Pricing API names each offer's seller only by an opaque Amazon
 * seller id (like the invented `ATESTSELLER01`). The Selling Partner API has no lookup from
 * that id to a business name, and the app never scrapes Amazon's pages, so the
 * owner recognizes a competitor two ways: by opening Amazon's own public seller
 * profile page for the id (built here), and by giving the id a name of their own
 * (the `seller_alias` table). See docs/offers.md, "Naming competitors".
 *
 * Shared so the API (for the digest mail) and the web (for every seller link)
 * build the same URL.
 */

/** The US marketplace id, the app's default when SPAPI_MARKETPLACE_ID is unset. */
export const US_MARKETPLACE_ID = 'ATVPDKIKX0DER';

/**
 * The public Amazon storefront host per marketplace id, for the marketplaces the
 * account's North America endpoint serves. The app imports one marketplace (the
 * US); an id not listed here falls back to the US host rather than guessing.
 */
const MARKETPLACE_HOST: ReadonlyMap<string, string> = new Map([
  ['ATVPDKIKX0DER', 'www.amazon.com'],
  ['A2EUQ1WTGCTBG2', 'www.amazon.ca'],
  ['A1AM78C64UM0Y8', 'www.amazon.com.mx'],
  ['A2Q3Y263D00KWC', 'www.amazon.com.br'],
]);

/**
 * An Amazon seller id as the app accepts it in a path: letters and digits only.
 * Real ids are 13–14 uppercase characters; the bound is loose so an invented
 * test id fits, and tight enough that nothing else rides in a URL.
 */
export const SELLER_ID_PATTERN = /^[A-Za-z0-9]{1,40}$/;

/**
 * A seller id as stored and compared: trimmed and uppercased. Amazon's ids are
 * uppercase, so a hand-typed lowercase id names the same seller rather than
 * starting a second row the offers join would never match.
 */
export function normalizeSellerId(sellerId: string): string {
  return sellerId.trim().toUpperCase();
}

/** The longest name and note the owner may give a seller. */
export const SELLER_NAME_MAX = 120;
export const SELLER_NOTE_MAX = 500;

/**
 * Any control character (Unicode category Cc: newlines, tabs, NUL, escape...).
 * Names and notes may not carry one: a name lands in the plain-text digest mail,
 * where a newline would let a typed name forge extra lines.
 */
export const CONTROL_CHARACTER = /\p{Cc}/u;

/** A seller name as stored: runs of whitespace collapsed to one space, ends trimmed. */
export function normalizeSellerName(name: string): string {
  return name.replace(/\s+/gu, ' ').trim();
}

/** The storefront host for a marketplace id; the US host for an unknown or empty one. */
export function marketplaceHost(marketplaceId: string | null | undefined): string {
  return (marketplaceId && MARKETPLACE_HOST.get(marketplaceId)) || 'www.amazon.com';
}

/**
 * Amazon's public seller profile page for a seller id: the page a shopper reaches
 * by clicking the "Sold by" name on a listing (`/sp?seller=<id>`), which shows the
 * seller's display name, ratings, and feedback. The app only links to it — it
 * never fetches it.
 */
export function sellerProfileUrl(
  sellerId: string,
  marketplaceId: string | null | undefined = US_MARKETPLACE_ID,
): string {
  return `https://${marketplaceHost(marketplaceId)}/sp?seller=${encodeURIComponent(sellerId)}`;
}

/** How a seller reads in text: "Name (id)" when the owner named it, else the id. */
export function sellerLabel(sellerId: string, name: string | null | undefined): string {
  return name ? `${name} (${sellerId})` : sellerId;
}
